/**
 * @fileoverview Common interceptors for the API client
 * Integrates with existing auth, error handling, and logging infrastructure
 */

import type { RequestInterceptor, ResponseInterceptor, ApiError } from './types';

// Authentication interceptor
export const createAuthInterceptor = (): RequestInterceptor => ({
  onRequest: async (config) => {
    // Get token from localStorage (existing auth pattern)
    const token = localStorage.getItem('authToken');
    
    if (token) {
      config.headers = {
        ...config.headers,
        'Authorization': `Bearer ${token}`
      };
    }
    
    return config;
  },
  
  onRequestError: async (error) => {
    throw error;
  }
});

// Logging interceptor for development
export const createLoggingInterceptor = (): RequestInterceptor & ResponseInterceptor => ({
  onRequest: async (config) => {
    if (import.meta.env.DEV) {
    }
    return config;
  },
  
  onResponse: async (response) => {
    if (import.meta.env.DEV) {
    }
    return response;
  },
  
  onResponseError: async (error) => {
    if (import.meta.env.DEV) {
    }
    throw error;
  }
});

// Error transformation interceptor (integrates with Phase 1.3 error handling)
export const createErrorTransformInterceptor = (): ResponseInterceptor => ({
  onResponseError: async (error: ApiError) => {
    // Transform API errors to match QuanthopError format for consistency
    const transformedError: ApiError = {
      ...error,
      type: 'api_error' as const,
      timestamp: error.timestamp || new Date().toISOString()
    };

    // Handle specific error cases
    switch (error.response?.status) {
      case 401:
        transformedError.code = 'UNAUTHORIZED';
        transformedError.message = 'Authentication required';
        // Trigger auth refresh/logout in real implementation
        break;
        
      case 403:
        transformedError.code = 'FORBIDDEN';
        transformedError.message = 'Access denied';
        break;
        
      case 404:
        transformedError.code = 'NOT_FOUND';
        transformedError.message = 'Resource not found';
        break;
        
      case 422:
        transformedError.code = 'VALIDATION_ERROR';
        transformedError.message = 'Validation failed';
        break;
        
      case 429:
        transformedError.code = 'RATE_LIMIT_EXCEEDED';
        transformedError.message = 'Too many requests';
        break;
        
      case 500:
        transformedError.code = 'INTERNAL_SERVER_ERROR';
        transformedError.message = 'Server error occurred';
        break;
        
      default:
        // Keep original error code and message
        break;
    }

    throw transformedError;
  }
});

// Request/Response timing interceptor
export const createTimingInterceptor = (): RequestInterceptor & ResponseInterceptor => {
  const timings = new Map<string, number>();
  
  return {
    onRequest: async (config) => {
      const requestId = `${config.method}-${Date.now()}-${Math.random()}`;
      timings.set(requestId, Date.now());
      
      // Add request ID to config for tracking
      config.headers = {
        ...config.headers,
        'X-Request-ID': requestId
      };
      
      return config;
    },
    
    onResponse: async (response) => {
      const requestId = response.config.headers?.['X-Request-ID'];
      if (requestId && timings.has(requestId)) {
        const startTime = timings.get(requestId)!;
        const duration = Date.now() - startTime;
        timings.delete(requestId);
        
        if (import.meta.env.DEV) {
        }
        
        // Could send to analytics/monitoring here
      }
      
      return response;
    },
    
    onResponseError: async (error) => {
      const requestId = error.config?.headers?.['X-Request-ID'];
      if (requestId && timings.has(requestId)) {
        const startTime = timings.get(requestId)!;
        const duration = Date.now() - startTime;
        timings.delete(requestId);
        
      }
      
      throw error;
    }
  };
};

// Retry policy interceptor
export const createRetryInterceptor = (maxRetries: number = 3): ResponseInterceptor => ({
  onResponseError: async (error: ApiError) => {
    const shouldRetry = (
      error.code === 'NETWORK_ERROR' ||
      error.code === 'TIMEOUT' ||
      (error.response?.status && error.response.status >= 500)
    );
    
    const attempt = error.details && typeof error.details === 'object' && 'attempt' in error.details
      ? (error.details.attempt as number)
      : 0;
    
    if (shouldRetry && attempt < maxRetries) {
      // Let the BaseApiClient handle the retry
      throw error;
    }
    
    // Add retry exhausted information
    if (attempt >= maxRetries) {
      error.message = `${error.message} (${maxRetries} retries exhausted)`;
    }
    
    throw error;
  }
});

// Cache interceptor for GET requests
export const createCacheInterceptor = (ttl: number = 5 * 60 * 1000): RequestInterceptor & ResponseInterceptor => {
  const cache = new Map<string, { data: unknown; timestamp: number }>();
  
  const getCacheKey = (config: { method?: string; params?: unknown; url?: string }): string => {
    return `${config.method}-${JSON.stringify(config.params || {})}-${config.url || ''}`;
  };
  
  return {
    onRequest: async (config) => {
      if (config.method === 'GET' || !config.method) {
        const cacheKey = getCacheKey(config);
        const cached = cache.get(cacheKey);
        
        if (cached && (Date.now() - cached.timestamp) < ttl) {
          // Return cached response (this would need special handling in real implementation)
        }
      }
      
      return config;
    },
    
    onResponse: async (response) => {
      if ((response.config.method === 'GET' || !response.config.method) && response.status === 200) {
        const cacheKey = getCacheKey(response.config);
        cache.set(cacheKey, {
          data: response.data,
          timestamp: Date.now()
        });
      }
      
      return response;
    }
  };
};

// Default interceptor configuration
export const createDefaultInterceptors = () => [
  createAuthInterceptor(),
  createLoggingInterceptor(),
  createTimingInterceptor(),
  createErrorTransformInterceptor(),
  createRetryInterceptor()
];
